import { CheckCircle, XCircle } from 'lucide-react'
import type { Exercise } from '@/types'
import EditableText from '@/components/edit/EditableText'
import { useEditMode } from '@/contexts/EditModeContext'
import { useAuth } from '@/contexts/AuthContext'
import { saveArrayItem, saveField } from '@/lib/editApi'

interface Props {
  exercise: Exercise
  selectedAnswer: string
  onSelectAnswer: (answer: string) => void
  isSubmitted: boolean
  isCorrect: boolean
  onExerciseUpdated?: (updated: Exercise) => void
}

export default function MultipleChoiceExercise({ exercise, selectedAnswer, onSelectAnswer, isSubmitted, isCorrect, onExerciseUpdated }: Props) {
  const options = exercise.options || []
  const { isEditMode } = useEditMode()
  const { profile } = useAuth()

  const saveOption = (index: number) => async (newText: string) => {
    if (!profile?.id) return { error: new Error('No autenticado') }
    const { error, newArray } = await saveArrayItem({
      table: 'exercises', id: exercise.id, field: 'options',
      currentArray: options, index, newItemValue: newText, editedBy: profile.id
    })
    if (!error) onExerciseUpdated?.({ ...exercise, options: newArray })
    return { error }
  }

  const saveCorrectAnswer = async (newText: string) => {
    if (!profile?.id) return { error: new Error('No autenticado') }
    const { error } = await saveField({
      table: 'exercises', id: exercise.id, field: 'correct_answer',
      oldValue: exercise.correct_answer || '', newValue: newText, editedBy: profile.id
    })
    if (!error) onExerciseUpdated?.({ ...exercise, correct_answer: newText })
    return { error }
  }

  return (
    <div className="space-y-3">
      {options.map((option, index) => {
        const isSelected = selectedAnswer === option
        const showCorrect = isSubmitted && option === (exercise.correct_answer || '')
        const showIncorrect = isSubmitted && isSelected && !isCorrect
        const baseClass = `w-full p-4 rounded-xl border-2 text-left flex items-center gap-3 ${
          showCorrect ? 'border-green-500 bg-green-50'
            : showIncorrect ? 'border-red-500 bg-red-50'
            : isSelected ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200'
        } ${isEditMode ? '' : 'transition-all hover:border-gray-300'}`

        const badge = (
          <span className={`w-8 h-8 rounded-full flex items-center justify-center font-medium shrink-0 ${
            showCorrect ? 'bg-green-500 text-white'
              : showIncorrect ? 'bg-red-500 text-white'
              : isSelected ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-600'
          }`}>
            {String.fromCharCode(65 + index)}
          </span>
        )

        const inner = (
          <>
            {badge}
            <EditableText
              as="span"
              value={option}
              customSave={saveOption(index)}
              placeholder="(opción vacía)"
              className="flex-1 font-medium"
            />
            {showCorrect && <CheckCircle className="w-6 h-6 text-green-500" />}
            {showIncorrect && <XCircle className="w-6 h-6 text-red-500" />}
          </>
        )

        // En modo edición usamos un <div> en vez de <button> porque
        // <button disabled> suprime clicks en TODOS sus hijos (HTML5),
        // lo que impedía que el EditableText recibiera el click.
        if (isEditMode) {
          return (
            <div key={index} className={baseClass}>
              {inner}
            </div>
          )
        }

        return (
          <button
            key={index}
            type="button"
            onClick={() => !isSubmitted && onSelectAnswer(option)}
            disabled={isSubmitted}
            className={baseClass}
          >
            {inner}
          </button>
        )
      })}

      {isEditMode && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs font-medium text-amber-700 mb-1">Respuesta correcta:</p>
          <EditableText
            as="div"
            value={exercise.correct_answer}
            customSave={saveCorrectAnswer}
            placeholder="(sin respuesta correcta)"
            className="text-sm font-semibold text-amber-900"
          />
          <p className="text-xs text-amber-600 mt-1">
            Debe coincidir exactamente con una de las opciones.
          </p>
        </div>
      )}
    </div>
  )
}
